import { uploadJobPDF, savePDFUrlToJob } from '../services/pdf-storage';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getInventoryCode } from '../calculations/inventoryCode';
import { generateItemNumbers } from '../calculations/itemNumbering';

const BRAND_BROWN     = [139, 69, 19];
const BRAND_LIGHT     = [200, 160, 110];
const BRAND_CREAM     = [250, 247, 243];
const WHITE           = [255, 255, 255];
const LIGHT_GRAY      = [235, 230, 225];
const DARK_GRAY       = [80, 80, 80];
const MID_GRAY        = [160, 160, 160];
const DARK            = [30, 30, 30];
const LABEL_COLOR     = [100, 60, 20];
const TABLE_HEAD_BG   = [245, 240, 235];
const TABLE_HEAD_TEXT = [80, 40, 10];
const ROW_ALT         = [252, 250, 248];

const PAGE_W   = 279;
const PAGE_H   = 216;
const MARGIN   = 14;
const HEADER_H = 66;  // tall enough for name + phone + full address
const FOOTER_H = 12;

function toFrac(val) {
  if (val === null || val === undefined || val === '') return '—';
  const n = parseFloat(val);
  if (isNaN(n)) return '—';
  const whole = Math.floor(n);
  const dec   = n - whole;
  const fracs = [
    [0,''], [0.0625,'1/16'], [0.125,'1/8'], [0.1875,'3/16'],
    [0.25,'1/4'], [0.3125,'5/16'], [0.375,'3/8'], [0.4375,'7/16'],
    [0.5,'1/2'], [0.5625,'9/16'], [0.625,'5/8'], [0.6875,'11/16'],
    [0.75,'3/4'], [0.8125,'13/16'], [0.875,'7/8'], [0.9375,'15/16'],
  ];
  let closest = fracs[0], minDiff = Math.abs(dec - fracs[0][0]);
  for (const f of fracs) {
    const diff = Math.abs(dec - f[0]);
    if (diff < minDiff) { minDiff = diff; closest = f; }
  }
  if (closest[1] === '') return whole === 0 ? '0' : String(whole);
  return whole === 0 ? closest[1] : `${whole} ${closest[1]}`;
}

function formatDim(w, h) {
  return `${toFrac(w)} x ${toFrac(h)}`;
}

function screenLabel(w) {
  if (w.flexScreen) {
    if (w.screenSize && w.screenSize.toString().toLowerCase().includes('half')) return 'Half';
    return 'Full';
  }
  if (!w.screenType || w.screenType === 'None') return '';
  if (w.screenSize && w.screenSize.toLowerCase().includes('half')) return 'Half';
  return 'Full';
}

function flangesLabel(w) {
  const fin = w.finType || '';
  if (!fin) return '';
  return fin.toLowerCase().includes('without') ? '' : 'Yes';
}

function drawHeader(doc, job) {
  doc.setFillColor(...WHITE);
  doc.rect(0, 0, PAGE_W, HEADER_H, 'F');
  doc.setFillColor(...BRAND_BROWN);
  doc.rect(0, 0, 3, HEADER_H, 'F');

  try {
    doc.addImage('/brand/twp-logo.png', 'PNG', MARGIN + 2, 5, 22, 22);
  } catch (e) {}

  doc.setTextColor(...BRAND_BROWN);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('The Window Place LLC', MARGIN + 27, 12);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...LABEL_COLOR);
  doc.text('1300 E 4th Ave  •  Milbank, SD 57252-1547', MARGIN + 27, 17.5);
  doc.text('Phone: (605) 432-1834  •  Fax: (866) 432-1718', MARGIN + 27, 22);

  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...DARK);
  doc.text('Shop Order', PAGE_W - MARGIN, 16, { align: 'right' });

  const boxW = 72, boxH = 16, boxX = PAGE_W - MARGIN - boxW, boxY = 19;
  doc.setDrawColor(...BRAND_LIGHT);
  doc.setLineWidth(0.6);
  doc.roundedRect(boxX, boxY, boxW, boxH, 2, 2, 'S');
  doc.setFillColor(...TABLE_HEAD_BG);
  doc.roundedRect(boxX, boxY, boxW, 6, 2, 2, 'F');
  doc.rect(boxX, boxY + 3, boxW, 3, 'F');
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...TABLE_HEAD_TEXT);
  doc.text('Order Number', boxX + boxW * 0.25, boxY + 4.2, { align: 'center' });
  doc.text('Date',         boxX + boxW * 0.75, boxY + 4.2, { align: 'center' });
  doc.setDrawColor(...BRAND_LIGHT);
  doc.line(boxX + boxW / 2, boxY + 6, boxX + boxW / 2, boxY + boxH);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...DARK);
  doc.text(job.woNumber || '—', boxX + boxW * 0.25, boxY + 13, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  const dateDisplay = job.orderDate
    ? new Date(job.orderDate + 'T12:00:00').toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
    : '—';
  doc.text(dateDisplay, boxX + boxW * 0.75, boxY + 13, { align: 'center' });

  // ── Divider ──
  doc.setDrawColor(...LIGHT_GRAY);
  doc.setLineWidth(0.4);
  doc.line(MARGIN, 36, PAGE_W - MARGIN, 36);

  // ── CUSTOMER INFORMATION label ──
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND_BROWN);
  doc.text('CUSTOMER INFORMATION', MARGIN + 2, 41);

  // ── Customer Name ──
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...DARK);
  doc.text(job.customerName || '—', MARGIN + 2, 47);

  // ── Full address — stored as one combined string in Firestore ──
  const fullAddress = job.address || null;

  // ── Phone ──
  const phone = job.phoneNumber || job.phone || null;

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...DARK_GRAY);

  if (phone && fullAddress) {
    doc.text(`Phone: ${phone}`, MARGIN + 2, 53);
    doc.text(`Address: ${fullAddress}`, MARGIN + 60, 53);
  } else if (phone) {
    doc.text(`Phone: ${phone}`, MARGIN + 2, 53);
  } else if (fullAddress) {
    doc.text(`Address: ${fullAddress}`, MARGIN + 2, 53);
  }

  doc.setFillColor(...BRAND_BROWN);
  doc.rect(0, HEADER_H - 1, PAGE_W, 1, 'F');
}

function drawFooter(doc, pageNum, totalPages) {
  const y = PAGE_H - FOOTER_H;
  doc.setFillColor(...WHITE);
  doc.rect(0, y, PAGE_W, FOOTER_H, 'F');
  doc.setFillColor(...BRAND_BROWN);
  doc.rect(0, y, PAGE_W, 0.8, 'F');
  doc.rect(0, y, 3, FOOTER_H, 'F');
  doc.setTextColor(...LABEL_COLOR);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text('The Window Place LLC  •  1300 E 4th Ave, Milbank, SD 57252  •  (605) 432-1834', PAGE_W / 2, y + 5, { align: 'center' });
  const now = new Date();
  const printed = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + '  ' + now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  doc.setTextColor(...MID_GRAY);
  doc.setFontSize(6.5);
  doc.text(`Printed: ${printed}  •  Page ${pageNum} of ${totalPages}`, PAGE_W - MARGIN, y + 9, { align: 'right' });
}

// ── returnBlob: true → returns Blob (for zip), false → saves file directly ──
export function generateShopOrderPDF(job, calculatedWindows, returnBlob = false) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });

  const rawRows = [];
  calculatedWindows.forEach(w => {
    const { code, series } = getInventoryCode(w);
    const noteParts = [];
    if (w.options) noteParts.push(w.options);
    if (w.grids) {
      // V/H bar counts live on the glass panes (w.glass[]). For DH, glass[0]=Keeper, glass[1]=Lock.
      // Show "Both sashes V3 × H1" when sashes match, "Keeper V3 × H1 · Lock V2 × H1" when they differ.
      const glass = Array.isArray(w.glass) ? w.glass : [];
      const firstGlass = glass[0] || null;
      const gPattern = w.gridPattern || (firstGlass && firstGlass.gridPattern) || '';
      const gColor   = w.gridColor   || (firstGlass && firstGlass.gridColor)   || '';
      let bars = '';
      if (glass.length >= 2) {
        const k = glass[0] || {};
        const l = glass[1] || {};
        const kV = k.gridQtyV || 0, kH = k.gridQtyH || 0;
        const lV = l.gridQtyV || 0, lH = l.gridQtyH || 0;
        if (kV === lV && kH === lH) {
          bars = `Both sashes V${kV} × H${kH}`;
        } else {
          bars = `Keeper V${kV} × H${kH} · Lock V${lV} × H${lH}`;
        }
      } else {
        const gV = w.gridQtyV || (firstGlass && firstGlass.gridQtyV) || 0;
        const gH = w.gridQtyH || (firstGlass && firstGlass.gridQtyH) || 0;
        bars = `V${gV} × H${gH}`;
      }
      const parts = ['Grids:', gPattern, '·', bars];
      if (gColor) parts.push('·', gColor);
      noteParts.push(parts.filter(Boolean).join(' ').replace(/\s+/g, ' ').trim());
    }
    rawRows.push({
      qty:             w.qty    || 1,
      width:           w.overallW,
      height:          w.height,
      series,
      code,
      screen:          screenLabel(w),
      flanges:         flangesLabel(w),
      notes:           '',  // Always empty — AJ writes notes by hand on printed sheet
      isMulledPane:    !!w.isMulledPane,
      mulledUnitLabel: w.mulledUnitLabel || null,
    });
  });

  // ROLLUP RULE (matches rollup-windows.js):
  //   Standalone windows NEVER merge with each other — even if they share
  //   the same code + size + screen + flanges. Two same-size standalone
  //   windows may differ in glass package, tempered status, or other fields
  //   not surfaced into the shop order row. Merging them silently makes the
  //   shop build the wrong count.
  //
  //   Mulled panes ONLY merge when they share the SAME mulledUnitLabel AND
  //   all other specs match. This correctly handles qty>1 of a joined assembly.
  //
  //   Then PW rows sort to the top (AJ's preference).
  const groupKey = (r, idx) => {
    if (!r.isMulledPane) return `__standalone_${idx}`;
    const label = String(r.mulledUnitLabel || '').toLowerCase();
    return [
      'M', label,
      String(r.code||'').toLowerCase(),
      parseFloat(r.width)||'',
      parseFloat(r.height)||'',
      String(r.screen||'').toLowerCase(),
      String(r.flanges||'').toLowerCase(),
      String(r.notes||'').toLowerCase(),
    ].join('|');
  };
  const isPW = r => String(r.code||'').toLowerCase().startsWith('pw') || String(r.series||'').toLowerCase().includes('picture');
  const groups = new Map();
  rawRows.forEach((r, i) => {
    const k = groupKey(r, i);
    const qty = parseFloat(r.qty) || 1;
    if (groups.has(k)) {
      groups.get(k).qty += qty;
    } else {
      groups.set(k, { row: { ...r }, qty, firstIdx: i });
    }
  });
  // ENTRY ORDER — print rows in the exact order they were entered (by firstIdx,
  // i.e. item number 8963-1, -2, -3 ...). Previously PW rows were floated to the
  // top, which moved windows off the line they were entered on. Shop builds in
  // entry sequence, so no type-based reordering. Merge (mulled-only) is unchanged.
  const collapsed = [...groups.values()]
    .sort((a, b) => a.firstIdx - b.firstIdx)
    .map(g => ({ ...g.row, qty: g.qty }));
  rawRows.length = 0;
  rawRows.push(...collapsed);

  const numbered  = generateItemNumbers(job.woNumber || 'DRAFT', rawRows);
  const totalQty  = numbered.reduce((a, r) => a + parseFloat(r.qty || 1), 0);
  const ROWS_PER_PAGE = 20; // reduced slightly to account for taller header
  const pages = [];
  for (let i = 0; i < numbered.length; i += ROWS_PER_PAGE) pages.push(numbered.slice(i, i + ROWS_PER_PAGE));
  if (pages.length === 0) pages.push([]);
  const totalPages = pages.length;

  pages.forEach((pageRows, pageIdx) => {
    if (pageIdx > 0) doc.addPage();
    drawHeader(doc, job);
    drawFooter(doc, pageIdx + 1, totalPages);

    autoTable(doc, {
      startY: HEADER_H + 4,
      head: [['Qty', 'W x H', 'Series', 'Code', 'Item #', 'Screen', 'Flanges', 'Notes']],
      body: pageRows.map(r => [
        r.qty,
        formatDim(r.width, r.height),
        r.series,
        r.code,
        r.itemNumber,
        r.screen  || '',
        r.flanges || '',
        r.notes   || '',
      ]),
      styles: {
        fontSize: 8, cellPadding: { top: 4, bottom: 4, left: 4, right: 4 },
        textColor: DARK, lineColor: LIGHT_GRAY, lineWidth: 0.2,
        valign: 'middle', font: 'helvetica', overflow: 'linebreak',
      },
      headStyles: {
        fillColor: TABLE_HEAD_BG, textColor: TABLE_HEAD_TEXT, fontStyle: 'bold',
        fontSize: 7, halign: 'center', lineColor: BRAND_LIGHT, lineWidth: 0.5,
        cellPadding: { top: 5, bottom: 5, left: 4, right: 4 }, minCellHeight: 10, overflow: 'linebreak',
      },
      alternateRowStyles: { fillColor: ROW_ALT },
      columnStyles: {
        0: { cellWidth: 12,     halign: 'center' },
        1: { cellWidth: 28,     halign: 'center' },
        2: { cellWidth: 68,     halign: 'left'   },
        3: { cellWidth: 24,     halign: 'center' },
        4: { cellWidth: 18,     halign: 'center' },
        5: { cellWidth: 18,     halign: 'center' },
        6: { cellWidth: 18,     halign: 'center' },
        7: { cellWidth: 'auto', halign: 'left'   },
      },
      margin: { left: MARGIN, right: MARGIN, bottom: FOOTER_H + 6 },
      tableLineColor: LIGHT_GRAY, tableLineWidth: 0.2,
      didDrawRow: (data) => {
        if (data.section === 'body') {
          doc.setFillColor(...BRAND_LIGHT);
          doc.rect(MARGIN, data.row.y, 2, data.row.height, 'F');
        }
      },
    });

    if (pageIdx === totalPages - 1) {
      const finalY = doc.lastAutoTable.finalY;
      doc.setFillColor(...TABLE_HEAD_BG);
      doc.rect(MARGIN, finalY, 12, 9, 'F');
      doc.setTextColor(...BRAND_BROWN);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(String(totalQty), MARGIN + 6, finalY + 6, { align: 'center' });
      doc.setFillColor(...BRAND_CREAM);
      doc.rect(MARGIN + 12, finalY, PAGE_W - MARGIN * 2 - 12, 9, 'F');
      doc.setTextColor(...LABEL_COLOR);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text(`Total Windows: ${totalQty}`, MARGIN + 18, finalY + 6);
      doc.setDrawColor(...BRAND_LIGHT);
      doc.setLineWidth(0.5);
      doc.rect(MARGIN, finalY, PAGE_W - MARGIN * 2, 9, 'S');
    }
  });

  const customer = (job.customerName || 'Customer').replace(/\s+/g, '_');
  const wo       = job.woNumber || 'DRAFT';
  const filename = `${customer}_WO${wo}_ShopOrder.pdf`;

  // ── Fire-and-forget upload to Firebase Storage ────────────────────────────
  const _blob = doc.output('blob');
  const _contractNum = (job.woNumber || 'DRAFT').replace(/\s+/g, '_');
  uploadJobPDF(_contractNum, 'shoporder', _blob)
    .then(url => savePDFUrlToJob(job.twpJobId || null, job.id || null, 'shoporder', url))
    .catch(e => console.warn('PDF upload skipped:', e));
  if (returnBlob) return doc.output('blob');
  doc.save(filename);
}